import { Header, SearchFilterWrapper, setDateFromStorage } from '@fareye/common'

import { IDateRange } from '@fareye/types'
import { DatePicker, Flex, Grid } from '@fareye/ui'
import {
  currenthhmmA,
  dMmm,
  getQueryParams,
  getSearchFilterFromSession,
  isEmpty,
  yyyyMmDd
} from '@fareye/utils'
import queryString, { ParsedQuery } from 'query-string'
import React from 'react'
import { mapping } from '../config/mapComponents'

import { IInTransitPerformance } from '../utility/types'
import { TransportModeFilter } from '../components/TransportModeFilter'
import SummaryFilter from '../components/Summary'

export function LayoutWithoutType({
  match,
  config,
  baseUrl,
  history,
  location,
  routesConfig
}: IInTransitPerformance) {
  const [isFirst, setIsFirst] = React.useState(false)
  const searchParams: ParsedQuery<string | Date> = queryString.parse(
    location.search
  )
  const {
    links,
    carryDate,
    showDatePicker,
    showSummary,
    showTimezone,
    toggleContent
  } = config.config

  const { children, styling, globalSearchPageId } = config
  const { style, className, bodyBgColor, variant, layoutStyle } = styling
  const [globalSearch, setGlobalSearch] = React.useState(
    getSearchFilterFromSession(globalSearchPageId)
  )
  const [summaryFilter, setSummaryFilter] = React.useState<any>([])
  const [summaryData, setSummaryData] = React.useState([])
  const [transportModeFilter, setTransportModeFilter] = React.useState<any>([])
  const [selectedMode, setSelectedMode] = React.useState('All')
  const getDefaultIndex = (element: any) =>
    element['label']?.toLowerCase().startsWith('total')
  const defaultIndex = summaryData.findIndex(getDefaultIndex)

  React.useEffect(() => {
    if (isEmpty(summaryFilter) && !isEmpty(summaryData)) {
      if (defaultIndex !== -1) {
        setSummaryFilter(summaryData[defaultIndex])
      }
    }
  }, [summaryData])
  const filterSelectedKey = toggleContent
    ? toggleContent.filter((item: any) =>
        item.link ? item.link === match.path : true
      )
    : []
  const [selectedKey, setSelectedKey] = React.useState(
    filterSelectedKey.length ? filterSelectedKey[0].selectedKey : ''
  )

  const [lastUpdate, setLastUpdate] = React.useState<string>('')
  const [dateRange, setDateRange] = React.useState(() => {
    return setDateFromStorage({
      showDatePicker: !!showDatePicker,
      carryDate,
      searchParams
    })
  })

  const { startDate, endDate } = dateRange as IDateRange<Date>

  const query = {
    ...searchParams,
    startDate,
    endDate
  }

  const queryParams = getQueryParams(query)
  React.useEffect(() => {
    if (isFirst) {
      setIsFirst(true)
      history.push({
        pathname: match.url,
        search: queryParams
      })
    }
    setLastUpdate(currenthhmmA())
  }, [queryParams])

  React.useEffect(() => {
    if (showDatePicker) {
      let prevStartDate = searchParams.startDate
      let prevEndDate = searchParams.endDate
      if (prevStartDate !== startDate || prevEndDate !== endDate) {
        const queryParams = getQueryParams({
          ...queryString.parse(location.search),
          startDate,
          endDate
        })
        history.replace({
          pathname: match.url,
          search: queryParams
        })
      }
    }
  }, [dateRange])

  function setDateRangeAndStorage(dates: IDateRange<string | Date>) {
    localStorage.setItem('date', JSON.stringify(dates))
    setDateRange(dates as IDateRange<Date>)
    if (defaultIndex !== -1) {
      setSummaryFilter(summaryData[defaultIndex])
    }
  }

  function setGlobalSearchAndSummaryFilter(ele: any) {
    if (defaultIndex !== -1) {
      setSummaryFilter(summaryData[defaultIndex])
    }
    setGlobalSearch(ele)
  }

  const fileName = (match.params && (match.params as any).id) || 'InTransit'

  return (
    <Flex column width={1} style={{ ...layoutStyle, height: '100%' }}>
      <Header
        match={match}
        toggle={toggleContent}
        lastUpdate={lastUpdate}
        selectedKey={selectedKey}
        setSelectedKey={setSelectedKey}
        startDate={startDate}
        endDate={endDate}
        location={location}
        history={history}
        routesConfig={routesConfig}
        rightTopCorner={
          <Flex>
            {showDatePicker && (
              <DatePicker
                variant="fareyeV2"
                formatter={yyyyMmDd}
                onDateRangeSelected={setDateRangeAndStorage}
                value={dateRange}
              />
            )}
          </Flex>
        }
        showTimezone={showTimezone}
      >
        <Flex column width={1}>
          <Flex>
            <SearchFilterWrapper
              apiFilter={config.filters && (config.filters as any).apiFilter}
              customKeyMapping={(config.filters as any)?.customKeyMapping}
              apiFav={config.filters && (config.filters as any).apiFav}
              pageId="intransit"
              search={globalSearch}
              summaryKeys={['status', 'transport_mode']}
              setSearch={setGlobalSearchAndSummaryFilter}
              startDate={startDate}
              endDate={endDate}
              showTimezone={showTimezone}
            />
          </Flex>
          <Flex wrap justifyContentSpaceBetween>
            {showSummary && (
              <SummaryFilter
                summaryData={summaryData}
                summaryFilter={summaryFilter}
                setSummaryFilter={setSummaryFilter}
              />
            )}
            {!isEmpty(transportModeFilter) && (
              <TransportModeFilter
                summaryList={transportModeFilter}
                selectedMode={selectedMode}
                setSelectedMode={setSelectedMode}
              />
            )}
          </Flex>
        </Flex>
      </Header>
      <div style={{ flexGrow: 1, background: bodyBgColor }}>
        <Grid
          gridGap={variant.gridGap}
          gridTemplateColumns={variant.gridTemplateColumns}
          style={{ ...style }}
          className={className}
        >
          {Object.entries(children).map(([comp, val]: any) => {
            let ComponentRender: any = (mapping as any)[val.component]
            return (
              <ComponentRender
                key={comp}
                match={match}
                fileName={`${fileName}_${dMmm()}`}
                history={history}
                mapping={mapping}
                location={location}
                globalSearch={globalSearch}
                selectedKey={selectedKey}
                setSummaryFilter={setSummaryFilter}
                endDate={showDatePicker ? endDate : null}
                startDate={showDatePicker ? startDate : null}
                config={(config as any).children[comp]}
                setTransportModeFilter={setTransportModeFilter}
                transportModeFilter={transportModeFilter}
                selectedMode={selectedMode}
                setSummaryData={setSummaryData}
                summaryFilter={summaryFilter}
              />
            )
          })}
        </Grid>
      </div>
    </Flex>
  )
}
